/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: 图片预加载 Hook，支持批量预加载和加载状态追踪
 */
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseImagePreloadOptions {
  /** 是否立即开始预加载，默认 true */
  immediate?: boolean;
}

interface UseImagePreloadReturn {
  /** 所有图片是否加载完成 */
  isAllLoaded: boolean;
  /** 已加载的图片 URL 集合 */
  loadedUrls: Set<string>;
  /** 手动预加载单张图片 */
  preloadImage: (url: string) => Promise<void>;
  /** 手动预加载多张图片 */
  preloadImages: (urls: string[]) => Promise<void>;
}

/**
 * 图片预加载 Hook
 * @param urls 要预加载的图片 URL 数组
 * @param options 配置选项
 */
export const useImagePreload = (
  urls: string[],
  options: UseImagePreloadOptions = {},
): UseImagePreloadReturn => {
  const { immediate = true } = options;

  const [loadedUrls, setLoadedUrls] = useState<Set<string>>(new Set());
  const loadingRef = useRef<Set<string>>(new Set());

  // 预加载单张图片
  const preloadImage = useCallback(
    (url: string): Promise<void> => {
      return new Promise((resolve) => {
        // 已加载或正在加载，直接返回
        if (loadedUrls.has(url) || loadingRef.current.has(url)) {
          resolve();
          return;
        }

        // 标记为正在加载
        loadingRef.current.add(url);

        if (typeof window === 'undefined') {
          resolve();
          return;
        }

        const img = new window.Image();

        img.onload = () => {
          loadingRef.current.delete(url);
          setLoadedUrls((prev) => new Set(prev).add(url));
          resolve();
        };

        img.onerror = () => {
          loadingRef.current.delete(url);
          // 即使加载失败也标记为已处理，避免重复请求
          setLoadedUrls((prev) => new Set(prev).add(url));
          resolve();
        };

        img.src = url;
      });
    },
    [loadedUrls],
  );

  // 预加载多张图片
  const preloadImages = useCallback(
    async (imageUrls: string[]): Promise<void> => {
      await Promise.all(imageUrls.map(preloadImage));
    },
    [preloadImage],
  );

  // 组件挂载时自动预加载
  useEffect(() => {
    if (immediate && urls.length > 0) {
      urls.forEach(preloadImage);
    }
  }, [immediate, urls, preloadImage]);

  const isAllLoaded = urls.every((url) => loadedUrls.has(url));

  return {
    isAllLoaded,
    loadedUrls,
    preloadImage,
    preloadImages,
  };
};

export default useImagePreload;
